// Core
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { FieldSearchSelect, RemoteTable } from "components/AtomsV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetDsoAggregatorByCommunityIdPaginationQuery } from "state/dso/dsoApiSlice";

// Utils
import { useGetMeters } from "utils/crud/read";
import { datetimeToDate, datetimeToTime } from "utils/time";

const fmt = (v) => (v != null ? Number(v).toFixed(3) : "—");

const num = (v, color) => (
  <span className="tabular-nums" style={color ? { color, fontWeight: 600 } : undefined}>
    {fmt(v)}
  </span>
);

function ListDsoAggregator() {
  const community = useSelector(selectCurrentCommunity);
  const meters = useGetMeters("true");
  const { t } = useTranslation(["dso"]);

  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [meter, setMeter] = useState(null);

  const { data, isFetching } = useGetDsoAggregatorByCommunityIdPaginationQuery(
    {
      page,
      limit,
      date,
      time,
      meter,
      communityId: community.id,
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    {
      pollingInterval: 15000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const meterIdToIdentifier = (meterId) => meters?.find((m) => m.id === meterId)?.identifier;

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((row, i) => ({
        id: `${row.datetime_timestamp}-${row.meter_id}-${i}`,
        date: datetimeToDate(row.datetime_timestamp),
        time: datetimeToTime(row.datetime_timestamp),
        meter_identifier: meterIdToIdentifier(row.meter_id) ?? "—",
        total_surplus: row.total_surplus,
        sharing_energy: row.sharing_energy,
      })),
    [data, meters]
  );

  const columns = [
    { key: "date", label: t("table.columns.date"), sortable: true },
    { key: "time", label: t("table.columns.time"), sortable: true },
    {
      key: "meter_identifier",
      label: t("table.columns.meter_identifier"),
      render: (v) => <b>{v}</b>,
    },
    {
      key: "total_surplus",
      label: t("table.columns.total_surplus"),
      align: "right",
      render: (v) => num(v, "var(--warning)"),
    },
    {
      key: "sharing_energy",
      label: t("table.columns.sharing_energy"),
      align: "right",
      render: (v) => num(v, "var(--accent)"),
    },
  ];

  const allLabel = t("filters.all", { defaultValue: "Todos", ns: "common" });

  const dateOptions = [
    { value: "", label: allLabel },
    ...(data?.distinct_dates ?? []).map((o) => ({ value: o.date, label: o.date })),
  ];
  const timeOptions = [
    { value: "", label: allLabel },
    ...(data?.distinct_times ?? []).map((o) => ({ value: o.time, label: o.time })),
  ];
  const meterOptions = [
    { value: "", label: allLabel },
    ...(meters ?? []).map((m) => ({ value: m.id, label: m.identifier })),
  ];

  const filters = (
    <>
      <FieldSearchSelect
        label={t("table.columns.date")}
        value={date ?? ""}
        onChange={(v) => {
          setPage(0);
          setDate(v || null);
        }}
        options={dateOptions}
      />
      <FieldSearchSelect
        label={t("table.columns.time")}
        value={time ?? ""}
        onChange={(v) => {
          setPage(0);
          setTime(v || null);
        }}
        options={timeOptions}
      />
      <FieldSearchSelect
        label={t("table.columns.meter_identifier")}
        value={meter ?? ""}
        onChange={(v) => {
          setPage(0);
          setMeter(v || null);
        }}
        options={meterOptions}
      />
    </>
  );

  return (
    <div className="rec-card">
      <HelpFloatingButtonV2 helpScreen="dso-aggregator-list" />
      <RemoteTable
        columns={columns}
        rows={rows}
        totalCount={data?.total_count ?? 0}
        page={page}
        limit={limit}
        onPageChange={setPage}
        sort={sortOrder}
        onSortChange={setSortOrder}
        filters={filters}
        loading={isFetching}
        emptyText={t("table.no_data", { defaultValue: "Sem dados disponíveis.", ns: "common" })}
      />
    </div>
  );
}

export default ListDsoAggregator;
